import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Mail, Phone, Plane, Briefcase, History, 
  Send, CheckCircle, XCircle, Loader2, AlertTriangle,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a01f8d12-1bde-4fd2-8a5f-4dafd263008a/artifacts/d665bj3h_Global%20Wings%20fond%20noir.png";

const ProfileFormPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [prospectName, setProspectName] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [maritalStatuses, setMaritalStatuses] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    has_passport: null,
    travel_purpose: '',
    destination: '',
    profession: '',
    marital_status: '',
    has_children: null,
    owns_property: null,
    has_bank_account: null,
    had_visa_last_5_years: null,
    visa_refused: null,
    additional_message: ''
  });

  useEffect(() => {
    fetchFormData();
  }, [token]);

  const fetchFormData = async () => {
    try {
      const response = await api.get(`/profile/${token}`);
      setProspectName(response.data.prospect_name);
      setDestinations(response.data.destinations);
      setProfessions(response.data.professions);
      setMaritalStatuses(response.data.marital_statuses);
      setFormData(prev => ({ ...prev, full_name: response.data.prospect_name }));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response?.status === 410) {
        setError(err.response.data.detail || 'Ce lien a expiré ou a déjà été utilisé');
      } else if (err.response?.status === 404) {
        setError('Lien invalide');
      } else {
        setError('Une erreur est survenue');
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (formData.has_passport === null) {
      toast.error('Veuillez indiquer si vous avez un passeport');
      return;
    }
    if (!formData.travel_purpose || !formData.destination) {
      toast.error('Veuillez sélectionner l\'objet et la destination de votre voyage');
      return;
    }
    if (formData.travel_purpose === 'Visite') {
      if (!formData.profession || !formData.marital_status || 
          formData.has_children === null || formData.owns_property === null || 
          formData.has_bank_account === null) {
        toast.error('Veuillez compléter votre profil socio-professionnel');
        return;
      }
    }
    if (formData.had_visa_last_5_years === null || formData.visa_refused === null) {
      toast.error('Veuillez compléter votre historique de voyage');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/profile/${token}`, formData);
      setSubmitted(true);
      toast.success('Formulaire soumis avec succès !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  // Radio button component
  const RadioGroup = ({ label, value, onChange, options, required }) => (
    <div className="space-y-2">
      <label className="text-sm text-white font-medium">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="flex gap-4">
        {options.map((opt) => (
          <label 
            key={opt.value.toString()} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${
              value === opt.value 
                ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' 
                : 'border-white/10 bg-white/5 text-[#94A3B8] hover:border-white/20'
            }`}
          >
            <input
              type="radio"
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              value === opt.value ? 'border-[#D4AF37]' : 'border-[#64748B]'
            }`}>
              {value === opt.value && <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
            </div>
            <span className="text-sm">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  // Select component
  const SelectField = ({ label, value, onChange, options, placeholder, required }) => (
    <div className="space-y-2">
      <label className="text-sm text-white font-medium">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white appearance-none focus:outline-none focus:border-[#D4AF37] transition-colors"
        >
          <option value="" className="bg-[#0F172A]">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt} value={opt} className="bg-[#0F172A]">{opt}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none" />
      </div>
    </div>
  );

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            {error.includes('expiré') ? (
              <AlertTriangle className="w-10 h-10 text-amber-500" />
            ) : (
              <XCircle className="w-10 h-10 text-red-500" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">{error}</h1>
          <p className="text-[#94A3B8] mb-6">
            Veuillez contacter Global Wings Travel Consulting pour obtenir un nouveau lien.
          </p>
          <a href="tel:+243975608422">
            <Button className="btn-gold">
              <Phone className="w-4 h-4 mr-2" />
              Appeler +243 975 608 422
            </Button>
          </a>
        </motion.div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Formulaire envoyé !</h1>
          <p className="text-[#94A3B8] mb-6">
            Merci {formData.full_name}. Nous avons bien reçu votre demande et nous vous contacterons très prochainement avec un devis personnalisé.
          </p>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <img src={LOGO_URL} alt="Global Wings" className="h-20 mb-3" />
            <p className="text-[#94A3B8] text-sm">📞 +243 975 608 422</p>
            <p className="text-[#94A3B8] text-sm">📧 contact@globalwings-rdc.com</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">Formulaire de Demande de Devis</h1>
          <p className="text-[#94A3B8]">
            Remplissez ce formulaire détaillé pour recevoir un devis personnalisé pour votre demande de visa
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Identity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl border border-white/10 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <h2 className="text-lg font-semibold text-white">1. Identité</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-white font-medium">
                  Nom complet <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  placeholder="Prénom et Nom"
                  className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37] transition-colors"
                  data-testid="input-full-name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white font-medium">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="votre@email.com"
                    className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37] transition-colors"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="text-sm text-white font-medium">
                    Téléphone <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+243 XXX XXX XXX"
                    className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37] transition-colors"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <RadioGroup
                label="Avez-vous un passeport ?"
                value={formData.has_passport}
                onChange={(val) => handleChange('has_passport', val)}
                options={[
                  { value: true, label: 'Oui' },
                  { value: false, label: 'Non' }
                ]}
                required
              />
            </div>
          </motion.div>

          {/* Section 2: Travel Reason */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl border border-white/10 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                <Plane className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <h2 className="text-lg font-semibold text-white">2. Raison de Voyage</h2>
            </div>

            <div className="space-y-4">
              <SelectField
                label="Quel est l'objet de votre voyage ?"
                value={formData.travel_purpose}
                onChange={(val) => handleChange('travel_purpose', val)}
                options={['Études', 'Visite', 'Résidence Permanente']}
                placeholder="Sélectionner l'objet du voyage"
                required
              />

              <SelectField
                label="Où désirez-vous aller ?"
                value={formData.destination}
                onChange={(val) => handleChange('destination', val)}
                options={destinations}
                placeholder="Sélectionner la destination"
                required
              />
            </div>
          </motion.div>

          {/* Section 3: Socio-Professional Profile (only for "Visite") */}
          <AnimatePresence>
            {formData.travel_purpose === 'Visite' && (
              <motion.div
                initial={{ opacity: 0, y: 20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -20, height: 0 }}
                className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl border border-white/10 p-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">3. Profil Socio-Professionnel</h2>
                </div>

                <div className="space-y-4">
                  <SelectField
                    label="Votre profession"
                    value={formData.profession}
                    onChange={(val) => handleChange('profession', val)}
                    options={professions}
                    placeholder="Sélectionner votre profession"
                    required
                  />

                  <SelectField
                    label="Situation maritale"
                    value={formData.marital_status}
                    onChange={(val) => handleChange('marital_status', val)}
                    options={maritalStatuses}
                    placeholder="Sélectionner votre situation"
                    required
                  />

                  <RadioGroup
                    label="Avez-vous des enfants ?"
                    value={formData.has_children}
                    onChange={(val) => handleChange('has_children', val)}
                    options={[
                      { value: true, label: 'Oui' },
                      { value: false, label: 'Non' }
                    ]}
                    required
                  />

                  <RadioGroup
                    label="Possédez-vous un bien immobilier ?"
                    value={formData.owns_property}
                    onChange={(val) => handleChange('owns_property', val)}
                    options={[
                      { value: true, label: 'Oui' },
                      { value: false, label: 'Non' }
                    ]}
                    required
                  />

                  <RadioGroup
                    label="Avez-vous un compte bancaire avec des mouvements réguliers ?"
                    value={formData.has_bank_account}
                    onChange={(val) => handleChange('has_bank_account', val)}
                    options={[
                      { value: true, label: 'Oui' },
                      { value: false, label: 'Non' }
                    ]}
                    required
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Section 4: Travel History */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl border border-white/10 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                <History className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <h2 className="text-lg font-semibold text-white">
                {formData.travel_purpose === 'Visite' ? '4' : '3'}. Historique de Voyage
              </h2>
            </div>

            <div className="space-y-4">
              <RadioGroup
                label="Avez-vous obtenu un visa dans votre passeport durant les 5 dernières années ?"
                value={formData.had_visa_last_5_years}
                onChange={(val) => handleChange('had_visa_last_5_years', val)}
                options={[
                  { value: true, label: 'Oui' },
                  { value: false, label: 'Non' }
                ]}
                required
              />

              <RadioGroup
                label="Vous a-t-on déjà refusé un visa pour la destination que vous souhaitez ?"
                value={formData.visa_refused}
                onChange={(val) => handleChange('visa_refused', val)}
                options={[
                  { value: true, label: 'Oui' },
                  { value: false, label: 'Non' }
                ]}
                required
              />

              <div>
                <label className="text-sm text-white font-medium">
                  Message supplémentaire (optionnel)
                </label>
                <textarea
                  value={formData.additional_message}
                  onChange={(e) => handleChange('additional_message', e.target.value)}
                  placeholder="Informations complémentaires que vous souhaitez partager..."
                  rows={4}
                  className="w-full mt-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                  data-testid="input-message"
                />
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              type="submit"
              disabled={submitting}
              className="w-full btn-gold py-4 text-lg"
              data-testid="submit-profile-form"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Demander mon Devis Gratuit
                </>
              )}
            </Button>
          </motion.div>
        </form>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <img src={LOGO_URL} alt="Global Wings" className="h-14 mx-auto mb-2 opacity-60" />
          <p className="text-[#64748B] text-sm">
            +243 975 608 422
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfileFormPage;
